import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, BreadcrumbItem, Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import {
  createRole,
  listEmployeeRoleAssignments,
  listPermissions,
  listRoles,
  updateEmployeeRoles,
  updateRole,
} from '../services/roleService';
import { CreateRoleRequest, EmployeeRoleAssignment, Permission, Role, UpdateRoleRequest } from '../types/Role';

type RolesPageTab = 'roles' | 'assignments';

const rolesPageTabs: Array<{ id: RolesPageTab; label: string }> = [
  { id: 'roles', label: 'roleManagementTab' },
  { id: 'assignments', label: 'userAssignmentsTab' },
];

const emptyRoleForm: CreateRoleRequest = {
  name: '',
  description: '',
  isActive: true,
  permissionIds: [],
};

const toRoleForm = (role: Role): UpdateRoleRequest => ({
  name: role.name,
  description: role.description || '',
  isActive: role.isActive,
  permissionIds: role.permissions.map(permission => permission.id),
});

const groupedPermissions = (permissions: Permission[]): Array<{ category: string; items: Permission[] }> => {
  const map = new Map<string, Permission[]>();

  permissions.forEach(permission => {
    const category = (permission.category || 'General').trim() || 'General';
    const existing = map.get(category) || [];
    existing.push(permission);
    map.set(category, existing);
  });

  return Array.from(map.entries())
    .map(([category, items]) => ({
      category,
      items: [...items].sort((left, right) => left.name.localeCompare(right.name)),
    }))
    .sort((left, right) => left.category.localeCompare(right.category));
};

const normalizeRoleIds = (roleIds?: string[]): string[] => Array.from(new Set((roleIds || []).filter(Boolean))).sort();

const isAdminAccessPermission = (permission: Permission): boolean => permission.code === PermissionCodes.adminAccess;

const roleHasAdminAccess = (role: Role): boolean => role.permissions.some(isAdminAccessPermission);

const employeeHasAdminAccess = (employee: EmployeeRoleAssignment, rolesById: Record<string, Role>): boolean =>
  normalizeRoleIds(employee.roleIds).some(roleId => {
    const role = rolesById[roleId];
    return role ? roleHasAdminAccess(role) : false;
  });

export const RolesPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { hasPermission } = useAuth();

  const canManageRoles = hasPermission(PermissionCodes.rolesManage);
  const canAssignRoles = hasPermission(PermissionCodes.rolesAssign);
  const canGrantAdminAccess = hasPermission(PermissionCodes.adminAccess);

  const [activeTab, setActiveTab] = React.useState<RolesPageTab>('roles');

  const [roles, setRoles] = React.useState<Role[]>([]);
  const [permissions, setPermissions] = React.useState<Permission[]>([]);
  const [assignments, setAssignments] = React.useState<EmployeeRoleAssignment[]>([]);

  const [loadingRoles, setLoadingRoles] = React.useState(true);
  const [loadingPermissions, setLoadingPermissions] = React.useState(true);
  const [loadingAssignments, setLoadingAssignments] = React.useState(false);

  const [savingRole, setSavingRole] = React.useState(false);
  const [savingEmployeeId, setSavingEmployeeId] = React.useState<string>('');

  const [searchAssignments, setSearchAssignments] = React.useState('');

  const [roleForm, setRoleForm] = React.useState<CreateRoleRequest>(emptyRoleForm);
  const [editingRoleId, setEditingRoleId] = React.useState<string>('');
  const [showRoleForm, setShowRoleForm] = React.useState(false);

  const [roleError, setRoleError] = React.useState<string | null>(null);
  const [assignmentError, setAssignmentError] = React.useState<string | null>(null);

  const [assignmentDraftByEmployeeId, setAssignmentDraftByEmployeeId] = React.useState<Record<string, string[]>>({});

  const visiblePermissions = useMemo(
    () => permissions.filter(permission => permission.isActive && (canGrantAdminAccess || !isAdminAccessPermission(permission))),
    [permissions, canGrantAdminAccess],
  );
  const permissionGroups = useMemo(
    () => groupedPermissions(visiblePermissions).filter(group => group.items.length > 0),
    [visiblePermissions],
  );
  const visibleRoles = useMemo(
    () => (canGrantAdminAccess ? roles : roles.filter(role => !roleHasAdminAccess(role))),
    [roles, canGrantAdminAccess],
  );
  const roleById = useMemo(() => {
    return Object.fromEntries(roles.map(role => [role.id, role]));
  }, [roles]);
  const visibleAssignments = useMemo(
    () => (canGrantAdminAccess ? assignments : assignments.filter(employee => !employeeHasAdminAccess(employee, roleById))),
    [assignments, canGrantAdminAccess, roleById],
  );

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('admin', { ns: 'admin' }), path: '/admin' },
    { label: t('roles', { ns: 'admin' }), path: '/admin/roles' },
  ];

  const canCreateRole = roles.length === 0 || !showRoleForm;

  React.useEffect(() => {
    if (activeTab === 'assignments' && !canAssignRoles) {
      setActiveTab('roles');
    }
  }, [activeTab, canAssignRoles]);

  const loadRolesAndPermissions = React.useCallback(async () => {
    setLoadingRoles(true);
    setLoadingPermissions(true);
    setRoleError(null);

    try {
      const [roleData, permissionData] = await Promise.all([
        listRoles({ sortBy: 'name', sortDir: 'asc', page: 1, pageSize: 200 }),
        listPermissions(),
      ]);
      setRoles(roleData);
      setPermissions(permissionData);
    } catch (error) {
      setRoleError(error instanceof Error ? error.message : t('failedToLoadRoleManagementData', { ns: 'admin' }));
    } finally {
      setLoadingRoles(false);
      setLoadingPermissions(false);
    }
  }, [t]);

  const loadAssignments = React.useCallback(async () => {
    setLoadingAssignments(true);
    setAssignmentError(null);

    try {
      const data = await listEmployeeRoleAssignments({
        q: searchAssignments,
        page: 1,
        pageSize: 200,
      });

      setAssignments(data);
      setAssignmentDraftByEmployeeId(Object.fromEntries(data.map(item => [item.employeeId, normalizeRoleIds(item.roleIds)])));
    } catch (error) {
      setAssignmentError(error instanceof Error ? error.message : t('failedToLoadAssignments', { ns: 'admin' }));
    } finally {
      setLoadingAssignments(false);
    }
  }, [searchAssignments, t]);

  React.useEffect(() => {
    loadRolesAndPermissions();
  }, [loadRolesAndPermissions]);

  React.useEffect(() => {
    if (activeTab !== 'assignments') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      loadAssignments();
    }, 200);

    return () => window.clearTimeout(timeoutId);
  }, [activeTab, loadAssignments]);

  const beginCreateRole = () => {
    setEditingRoleId('');
    setRoleForm(emptyRoleForm);
    setRoleError(null);
    setShowRoleForm(true);
  };

  const beginEditRole = (role: Role) => {
    setEditingRoleId(role.id);
    setRoleForm(toRoleForm(role));
    setRoleError(null);
    setShowRoleForm(true);
  };

  const cancelRoleForm = () => {
    setEditingRoleId('');
    setRoleForm(emptyRoleForm);
    setRoleError(null);
    setShowRoleForm(false);
  };

  const toggleRolePermission = (permissionId: string) => {
    const permission = permissions.find(item => item.id === permissionId);
    if (permission && isAdminAccessPermission(permission) && !canGrantAdminAccess) {
      return;
    }

    setRoleForm(prev => {
      const existing = new Set(prev.permissionIds || []);
      if (existing.has(permissionId)) {
        existing.delete(permissionId);
      } else {
        existing.add(permissionId);
      }

      return {
        ...prev,
        permissionIds: Array.from(existing),
      };
    });
  };

  const saveRole = async () => {
    if (!canManageRoles) {
      setRoleError(t('noPermissionManageRoles', { ns: 'admin' }));
      return;
    }

    setSavingRole(true);
    setRoleError(null);

    let permissionIds = normalizeRoleIds(roleForm.permissionIds);
    const editingRole = editingRoleId ? roles.find(role => role.id === editingRoleId) : undefined;
    const adminAccessPermissionId = permissions.find(isAdminAccessPermission)?.id
      ?? editingRole?.permissions.find(isAdminAccessPermission)?.id;

    if (!canGrantAdminAccess && adminAccessPermissionId) {
      const rolePreviouslyHadAdminAccess = editingRole ? roleHasAdminAccess(editingRole) : false;

      if (rolePreviouslyHadAdminAccess) {
        permissionIds = normalizeRoleIds([...permissionIds, adminAccessPermissionId]);
      } else {
        permissionIds = permissionIds.filter(permissionId => permissionId !== adminAccessPermissionId);
      }
    }

    const payload: CreateRoleRequest = {
      name: roleForm.name?.trim(),
      description: roleForm.description?.trim(),
      isActive: roleForm.isActive,
      permissionIds,
    };

    try {
      if (editingRoleId) {
        await updateRole(editingRoleId, payload);
      } else {
        await createRole(payload);
      }

      await loadRolesAndPermissions();
      cancelRoleForm();
    } catch (error) {
      setRoleError(error instanceof Error ? error.message : t('failedToSaveRole', { ns: 'admin' }));
    } finally {
      setSavingRole(false);
    }
  };

  const toggleEmployeeRole = (employeeId: string, roleId: string) => {
    const role = roleById[roleId];
    if (role && roleHasAdminAccess(role) && !canGrantAdminAccess) {
      return;
    }

    setAssignmentDraftByEmployeeId(prev => {
      const current = new Set(prev[employeeId] || []);
      if (current.has(roleId)) {
        current.delete(roleId);
      } else {
        current.add(roleId);
      }

      return {
        ...prev,
        [employeeId]: normalizeRoleIds(Array.from(current)),
      };
    });
  };

  const saveEmployeeRoles = async (employeeId: string) => {
    if (!canAssignRoles) {
      setAssignmentError(t('noPermissionAssignRoles', { ns: 'admin' }));
      return;
    }

    setSavingEmployeeId(employeeId);
    setAssignmentError(null);

    try {
      let roleIds = normalizeRoleIds(assignmentDraftByEmployeeId[employeeId] || []);

      if (!canGrantAdminAccess) {
        const employee = assignments.find(item => item.employeeId === employeeId);
        const persistedAdminRoleIds = normalizeRoleIds(employee?.roleIds || [])
          .filter(roleId => {
            const role = roleById[roleId];
            return role ? roleHasAdminAccess(role) : false;
          });

        roleIds = normalizeRoleIds([...roleIds.filter(roleId => {
          const role = roleById[roleId];
          return role ? !roleHasAdminAccess(role) : true;
        }), ...persistedAdminRoleIds]);
      }

      await updateEmployeeRoles(employeeId, { roleIds });
      await loadAssignments();
    } catch (error) {
      setAssignmentError(error instanceof Error ? error.message : t('failedToUpdateEmployeeRoles', { ns: 'admin' }));
    } finally {
      setSavingEmployeeId('');
    }
  };

  const hasAssignmentChanges = (employee: EmployeeRoleAssignment): boolean => {
    const draft = normalizeRoleIds(assignmentDraftByEmployeeId[employee.employeeId] || []);
    const persisted = normalizeRoleIds(employee.roleIds || []);

    if (draft.length !== persisted.length) {
      return true;
    }

    return draft.some((roleId, index) => roleId !== persisted[index]);
  };

  const resetEmployeeRoles = (employee: EmployeeRoleAssignment) => {
    setAssignmentDraftByEmployeeId(prev => ({
      ...prev,
      [employee.employeeId]: normalizeRoleIds(employee.roleIds),
    }));
  };

  const getAssignedRoles = (employee: EmployeeRoleAssignment): Role[] => {
    return normalizeRoleIds(assignmentDraftByEmployeeId[employee.employeeId] || employee.roleIds)
      .map(roleId => roleById[roleId])
      .filter((role): role is Role => Boolean(role))
      .filter(role => canGrantAdminAccess || !roleHasAdminAccess(role));
  };

  return (
    <div className="company-admin-page roles-admin-page">
      <Breadcrumb items={breadcrumbItems} />

      <div className="company-page-head">
        <div className="company-tabs" role="tablist" aria-label={t('roleManagementTabs', { ns: 'admin' })}>
          {rolesPageTabs.filter(tab => tab.id !== 'assignments' || canAssignRoles).map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`company-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {t(tab.label, { ns: 'admin' })}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'roles' ? (
        <section className="company-section">
          <div className="company-section-head">
            <h3>{t('roles', { ns: 'admin' })}</h3>
            {canCreateRole && canManageRoles ? (
              <button type="button" className="btn btn-secondary" onClick={beginCreateRole}>{t('createRole', { ns: 'admin' })}</button>
            ) : null}
          </div>

          {roleError ? <div className="error">{roleError}</div> : null}

          {(loadingRoles || loadingPermissions) ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
              <Spinner text={t('loadingRoles', { ns: 'admin' })} />
            </div>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th>{t('role', { ns: 'admin' })}</th>
                    <th>{t('status', { ns: 'admin' })}</th>
                    <th>{t('permissions', { ns: 'admin' })}</th>
                    <th>{t('users', { ns: 'admin' })}</th>
                    <th>{t('actions', { ns: 'admin' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRoles.map(role => (
                    <tr key={role.id}>
                      <td>
                        <strong>{role.name}</strong>
                        {role.description ? <div style={{ color: '#64748b', marginTop: 4 }}>{role.description}</div> : null}
                        {role.isSystemRole ? <div style={{ marginTop: 6 }}><span className="badge">{t('systemRole', { ns: 'admin' })}</span></div> : null}
                      </td>
                      <td>{role.isActive ? t('active', { ns: 'admin' }) : t('inactive', { ns: 'admin' })}</td>
                      <td>{role.permissions.length}</td>
                      <td>{role.assignedEmployeeCount}</td>
                      <td>
                        {canManageRoles ? (
                          <button type="button" className="btn btn-secondary" onClick={() => beginEditRole(role)}>{t('edit', { ns: 'common' })}</button>
                        ) : (
                          <span style={{ color: '#64748b' }}>{t('viewOnly', { ns: 'admin' })}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {visibleRoles.length === 0 ? (
                    <tr>
                      <td colSpan={5}>{t('noRolesYet', { ns: 'admin' })}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>

              {showRoleForm ? (
                <div className="company-section" style={{ marginTop: 16 }}>
                  <div className="company-section-head">
                    <h3>{editingRoleId ? t('editRole', { ns: 'admin' }) : t('createRole', { ns: 'admin' })}</h3>
                  </div>

                  <div className="company-form-grid">
                    <label>
                      {t('name', { ns: 'admin' })}
                      <input
                        className="text-input"
                        value={roleForm.name || ''}
                        onChange={event => setRoleForm(prev => ({ ...prev, name: event.target.value }))}
                        placeholder={t('roleNamePlaceholder', { ns: 'admin' })}
                      />
                    </label>

                    <label>
                      {t('status', { ns: 'admin' })}
                      <select
                        className="text-input"
                        value={roleForm.isActive === false ? 'false' : 'true'}
                        onChange={event => setRoleForm(prev => ({ ...prev, isActive: event.target.value !== 'false' }))}
                      >
                        <option value="true">{t('active', { ns: 'admin' })}</option>
                        <option value="false">{t('inactive', { ns: 'admin' })}</option>
                      </select>
                    </label>

                    <label style={{ gridColumn: '1 / -1' }}>
                      {t('description', { ns: 'admin' })}
                      <textarea
                        className="text-input"
                        value={roleForm.description || ''}
                        onChange={event => setRoleForm(prev => ({ ...prev, description: event.target.value }))}
                        rows={3}
                      />
                    </label>
                  </div>

                  <div className="role-permissions-grid">
                    {permissionGroups.map(group => (
                      <div key={group.category} className="role-permission-group">
                        <h4>{group.category}</h4>
                        <div className="role-permission-list">
                          {group.items.map(permission => {
                            const checked = (roleForm.permissionIds || []).includes(permission.id);
                            return (
                              <label key={permission.id} className="role-permission-item">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={!canManageRoles || savingRole}
                                  onChange={() => toggleRolePermission(permission.id)}
                                />
                                <span>
                                  <strong>{permission.name}</strong>
                                  <small>{permission.code}</small>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="company-actions-inline" style={{ marginTop: 14 }}>
                    <button type="button" className="btn btn-secondary" onClick={cancelRoleForm} disabled={savingRole}>{t('cancel', { ns: 'common' })}</button>
                    <button type="button" className="btn btn-primary" onClick={saveRole} disabled={savingRole || !canManageRoles}>
                      {savingRole ? t('savingEllipsis', { ns: 'admin' }) : editingRoleId ? t('updateRole', { ns: 'admin' }) : t('createRole', { ns: 'admin' })}
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      {activeTab === 'assignments' ? (
        <section className="company-section">
          <div className="company-section-head">
            <h3>{t('userRoleAssignments', { ns: 'admin' })}</h3>
          </div>

          {assignmentError ? <div className="error">{assignmentError}</div> : null}

          <div className="company-toolbar" style={{ marginBottom: 14 }}>
            <input
              className="text-input"
              style={{ maxWidth: 320 }}
              placeholder={t('searchEmployeeByNameEmailTitle', { ns: 'admin' })}
              value={searchAssignments}
              onChange={event => setSearchAssignments(event.target.value)}
            />
          </div>

          {loadingAssignments ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
              <Spinner text={t('loadingAssignments', { ns: 'admin' })} />
            </div>
          ) : visibleRoles.length === 0 ? (
            <div>{t('noRolesFoundCreateFirst', { ns: 'admin' })}</div>
          ) : (
            <div className="role-assignment-cards">
              {visibleAssignments.map(employee => {
                const assignedRoles = getAssignedRoles(employee);
                const hasChanges = hasAssignmentChanges(employee);

                return (
                  <article key={employee.employeeId} className="role-assignment-card">
                    <div className="role-assignment-card-head">
                      <div>
                        <h4>{employee.employeeName}</h4>
                        <div className="role-assignment-meta">{employee.employeeEmail}</div>
                        {employee.employeeTitle ? <div className="role-assignment-meta">{employee.employeeTitle}</div> : null}
                      </div>

                      <div className="role-assignment-summary">
                        <span className="badge">{t('assignedRolesSummary', { ns: 'admin', assigned: assignedRoles.length, total: visibleRoles.length })}</span>
                        {hasChanges ? <span className="role-assignment-status">{t('unsavedChanges', { ns: 'admin' })}</span> : null}
                      </div>
                    </div>

                    <div className="role-assignment-current">
                      {assignedRoles.length > 0 ? assignedRoles.map(role => (
                        <span key={role.id} className="role-chip">{role.name}</span>
                      )) : <span className="role-assignment-empty">{t('noRolesAssigned', { ns: 'admin' })}</span>}
                    </div>

                    <details className="role-assignment-editor" open={hasChanges}>
                      <summary>{t('editRoleAccess', { ns: 'admin' })}</summary>
                      <div className="role-assignment-option-grid">
                        {visibleRoles.map(role => {
                          const checked = (assignmentDraftByEmployeeId[employee.employeeId] || []).includes(role.id);
                          return (
                            <label key={`${employee.employeeId}-${role.id}`} className={`role-assignment-option ${checked ? 'selected' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleEmployeeRole(employee.employeeId, role.id)}
                              />
                              <span>
                                <strong>{role.name}</strong>
                                {role.description ? <small>{role.description}</small> : null}
                              </span>
                            </label>
                          );
                        })}
                      </div>

                      <div className="company-actions-inline role-assignment-actions">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={!hasChanges || savingEmployeeId === employee.employeeId}
                          onClick={() => resetEmployeeRoles(employee)}
                        >
                          {t('reset', { ns: 'admin' })}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={!hasChanges || savingEmployeeId === employee.employeeId}
                          onClick={() => saveEmployeeRoles(employee.employeeId)}
                        >
                          {savingEmployeeId === employee.employeeId ? t('savingEllipsis', { ns: 'admin' }) : t('saveRoles', { ns: 'admin' })}
                        </button>
                      </div>
                    </details>
                  </article>
                );
              })}

              {visibleAssignments.length === 0 ? (
                <div className="role-assignment-empty-state">{t('noEmployeesFound', { ns: 'admin' })}</div>
              ) : null}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
};

export default RolesPage;
